// SupportTicketsView.jsx — View PURA da /support-tickets (View/ViewModel à risca).
// Recebe { vm, actions }. Contrato: supportTickets.vm.js (@version 1). Cobre loading, error,
// empty, ready, ticketExpanded, ticketExpandedNoReplies, detailsLoading e replySubmitting.
//
// ── DECISÃO DE DESIGN ────────────────────────────────────────────────────────
// Accordion foi descartado: ele esconde a conversa dentro da lista e faz a página pular a cada
// abertura. Aqui é MASTER–DETAIL em lg+ (fila de conversas à esquerda, conversa aberta à direita,
// ambas com scroll próprio) e, abaixo de lg, um caminho de PÁGINA ÚNICA: a lista dá lugar à
// conversa, com um botão "Voltar". Os dois caminhos leem os mesmos campos.
// A conversa é um FIO: mensagem original ancorada no topo, respostas do suporte à esquerda e as
// suas à direita, unidas por um trilho vertical. O editor é um composer fixo no pé do painel.
//
// Neutros vêm de --bg-page/--divider/--text-* (papéis themeáveis) e NÃO da escala --slate-*,
// que é global fixa e clara — usá-la como cor de texto quebra temas de superfície escura.
// Estado nunca é comunicado só por cor: todo tom carrega ícone e rótulo.
//
// ── DADOS QUE FALTAM (propostas p/ o VM — NÃO derivei nem inventei) ──────────
// 1. `journey: { stepIndex: number, stepLabels: string[] }` por ticket. Eu queria desenhar a
//    jornada Aberto → Em Análise → Aguardando → Resolvido → Fechado como progresso, mas isso
//    exige a POSIÇÃO do status na jornada. Derivar de statusLabel seria mapear string a passo e
//    quebraria em qualquer status novo. Por ora o status aparece como selo com ícone semântico.
// 2. `summary: { openCount, awaitingYouCount, unreadTotal }` no vm. Um resumo no topo ("2 aguardam
//    você") daria o "momento" que o brief pede, mas contar vm.tickets é agregação de negócio —
//    e a lista é paginada, então o total da página não é o total real.
// 3. `statusFilters: [{ id, label, tone, count }]` + `actions.onFilterStatus`. Uma fila de
//    conversas premium filtra por status; hoje não há campo nem callback.
// 4. `detail.lastActivityRelative`. No cabeçalho da conversa só tenho a data de abertura; "última
//    atividade" é o que o cliente procura ao reabrir um ticket antigo.
// Nada acima é bloqueante: a tela está completa sem eles.
import { useRef, useEffect } from 'react';
import {
  Box, Typography, Button, IconButton, CircularProgress, Tooltip, Skeleton, TextField,
} from '@mui/material';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import AddCommentRounded from '@mui/icons-material/AddCommentRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import ForumRounded from '@mui/icons-material/ForumRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';
import PriorityHighRounded from '@mui/icons-material/PriorityHighRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import NorthRounded from '@mui/icons-material/NorthRounded';
import SouthRounded from '@mui/icons-material/SouthRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import AlternateEmailRounded from '@mui/icons-material/AlternateEmailRounded';
import MarkChatUnreadRounded from '@mui/icons-material/MarkChatUnreadRounded';
import TouchAppRounded from '@mui/icons-material/TouchAppRounded';

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

// Tons semânticos: os tints são globais fixas (pares fundo-claro + texto-escuro), legíveis
// também sobre card escuro. O tom 'neutral' e 'primary' usam papéis themeáveis.
const TONE = {
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)' },
  error: { bg: 'var(--tint-error-bg)', border: 'var(--tint-error-border)', fg: 'var(--tint-error-fg)' },
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)' },
  primary: { bg: 'var(--color-primary-soft)', border: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)' },
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)' },
};
const tone = (t) => TONE[t] || TONE.neutral;

// Ícone por TOM do status (não por label): o VM define o tom, e cada tom tem um significado
// estável — assim um status novo continua ganhando ícone coerente sem a View adivinhar nada.
const STATUS_ICON = {
  info: MailOutlineRounded,
  warning: HourglassTopRounded,
  error: PriorityHighRounded,
  success: CheckCircleRounded,
  neutral: LockOutlined,
};

/* ── Selo de status ───────────────────────────────────────────────────── */
function StatusBadge({ label, statusTone, size = 'md' }) {
  const t = tone(statusTone);
  const Icon = STATUS_ICON[statusTone] || STATUS_ICON.neutral;
  const big = size === 'lg';
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625, flexShrink: 0,
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 999, px: big ? 1.5 : 1.125, py: big ? '5px' : '3px', maxWidth: '100%',
      }}
    >
      <Icon sx={{ fontSize: big ? 17 : 14, color: t.fg }} />
      <Typography
        variant={big ? 'body2' : 'caption'}
        sx={{ color: t.fg, fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap', lineHeight: 1.4 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/* ── Selo de prioridade (só quando priority.show) ─────────────────────── */
function PriorityBadge({ priority }) {
  if (!priority?.show) return null;
  const t = tone(priority.tone);
  const up = priority.arrow === '↑';
  const down = priority.arrow === '↓';
  const Arrow = up ? NorthRounded : down ? SouthRounded : null;
  // Alta/Urgente (tone 'error') pedem destaque. NÃO inverto o par do tint (fundo sólido em t.fg
  // com texto em --surface): em tema escuro --surface é escuro e o selo cai para 2.25:1. O destaque
  // vem de borda saliente + caixa alta + seta, mantendo o par fundo-claro/texto-escuro do tint (6.9:1).
  const loud = priority.tone === 'error';
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.375, flexShrink: 0,
        bgcolor: t.bg,
        border: loud ? '1.5px solid' : '1px solid', borderColor: loud ? t.fg : t.border,
        borderRadius: 999, px: 0.875, py: '3px',
      }}
    >
      {Arrow && <Arrow sx={{ fontSize: 13, color: t.fg }} />}
      <Typography
        variant="caption"
        sx={{
          color: t.fg, fontWeight: 'var(--fw-bold)',
          letterSpacing: '.03em', textTransform: 'uppercase', lineHeight: 1.4, whiteSpace: 'nowrap',
        }}
      >
        {priority.label}
      </Typography>
    </Box>
  );
}

/* ── Etiqueta de tipo (emoji vem do VM) ───────────────────────────────── */
function TypeTag({ label, typeTone }) {
  if (!label) return null;
  const t = tone(typeTone);
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'inline-flex', alignItems: 'center', flexShrink: 0,
        bgcolor: t.bg, color: t.fg, border: '1px solid', borderColor: t.border,
        borderRadius: 'var(--radius-sm)', px: 0.875, py: '2px',
        fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  );
}

/* ── Marca de "novas respostas" — o sinal que puxa o olho ─────────────── */
function UnreadPip({ label }) {
  if (!label) return null;
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
        bgcolor: 'var(--color-primary)', borderRadius: 999, px: 0.875, py: '2px',
        // o halo pulsa devagar; quem desativa animação no sistema recebe o selo estático
        animation: 'ticketPulse 2.4s ease-out infinite',
        '@keyframes ticketPulse': {
          '0%': { boxShadow: '0 0 0 0 var(--color-primary-light)' },
          '70%': { boxShadow: '0 0 0 6px transparent' },
          '100%': { boxShadow: '0 0 0 0 transparent' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <MarkChatUnreadRounded sx={{ fontSize: 13, color: 'var(--on-primary)' }} />
      <Typography variant="caption" sx={{ color: 'var(--on-primary)', fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap', lineHeight: 1.4 }}>
        {label}
      </Typography>
    </Box>
  );
}

/* ── Item da fila de conversas ────────────────────────────────────────── */
function TicketListItem({ ticket, selected, onSelect }) {
  const t = tone(ticket.statusTone);
  return (
    <Box
      component="li"
      role="button"
      tabIndex={0}
      aria-expanded={!!ticket.expanded}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      sx={{
        position: 'relative', cursor: 'pointer', listStyle: 'none',
        px: 2, py: 1.75,
        borderBottom: '1px solid', borderColor: 'var(--divider)',
        bgcolor: selected ? 'var(--color-primary-soft)' : 'transparent',
        transition: 'background-color .15s',
        outline: 'none',
        '&:hover': { bgcolor: 'var(--color-primary-soft)' },
        '&:focus-visible': { boxShadow: 'inset 0 0 0 2px var(--color-primary)' },
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {/* trilho de seleção — acompanha o item ativo, como a barra do menu lateral */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 3,
          bgcolor: selected ? 'var(--color-primary)' : 'transparent',
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
        <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.fg, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: t.fg, fontWeight: 'var(--fw-bold)' }}>
          {ticket.statusLabel}
        </Typography>
        <PriorityBadge priority={ticket.priority} />
        <Box sx={{ flex: 1 }} />
        <UnreadPip label={ticket.unreadLabel} />
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}
      >
        {ticket.subject}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
        <TypeTag label={ticket.typeLabel} typeTone={ticket.typeTone} />
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}>
          <ScheduleRounded sx={{ fontSize: 14, color: 'var(--text-secondary)' }} />
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
            {ticket.createdRelative}
          </Typography>
        </Box>
        {ticket.repliesLabel && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}>
            <ForumRounded sx={{ fontSize: 14, color: 'var(--text-secondary)' }} />
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              {ticket.repliesLabel}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* ── Skeleton da fila ─────────────────────────────────────────────────── */
function ListSkeleton() {
  return (
    <Box aria-label="Carregando seus tickets" role="status">
      {[0, 1, 2, 3].map((i) => (
        <Box key={i} sx={{ px: 2, py: 1.75, borderBottom: '1px solid', borderColor: 'var(--divider)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Skeleton variant="circular" width={8} height={8} sx={{ bgcolor: 'var(--divider)' }} />
            <Skeleton variant="text" width={88} sx={{ bgcolor: 'var(--divider)' }} />
          </Box>
          <Skeleton variant="text" sx={{ bgcolor: 'var(--divider)' }} />
          <Skeleton variant="text" width="62%" sx={{ bgcolor: 'var(--divider)' }} />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Skeleton variant="rounded" width={92} height={18} sx={{ bgcolor: 'var(--divider)' }} />
            <Skeleton variant="rounded" width={64} height={18} sx={{ bgcolor: 'var(--divider)' }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ── Skeleton da conversa ─────────────────────────────────────────────── */
function ThreadSkeleton() {
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }} role="status" aria-label="Carregando a conversa">
      <Box sx={{ ...CARD_SX, p: 2, boxShadow: 'none', bgcolor: 'var(--bg-page)' }}>
        <Skeleton variant="text" width={132} sx={{ bgcolor: 'var(--divider)' }} />
        <Skeleton variant="text" sx={{ bgcolor: 'var(--divider)' }} />
        <Skeleton variant="text" width="78%" sx={{ bgcolor: 'var(--divider)' }} />
      </Box>
      {[0, 1].map((i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: i % 2 ? 'flex-end' : 'flex-start' }}>
          <Box sx={{ width: '76%', maxWidth: 440 }}>
            <Skeleton variant="text" width={104} sx={{ bgcolor: 'var(--divider)' }} />
            <Skeleton variant="rounded" height={i % 2 ? 52 : 76} sx={{ bgcolor: 'var(--divider)', borderRadius: 'var(--radius-card)' }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ── Bolha de resposta ────────────────────────────────────────────────── */
function ReplyBubble({ reply }) {
  const mine = reply.author === 'you';
  return (
    <Box component="li" sx={{ listStyle: 'none', display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
      <Box sx={{ maxWidth: { xs: '100%', sm: '82%' }, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5,
            flexDirection: mine ? 'row-reverse' : 'row',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'var(--fw-bold)', color: mine ? 'var(--color-primary-dark)' : 'var(--text-primary)' }}>
            {reply.authorLabel}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
            {reply.createdRelative}
          </Typography>
        </Box>
        <Box
          // contentHtml chega JÁ SANITIZADO do VM (contrato) — a View apenas injeta.
          dangerouslySetInnerHTML={{ __html: reply.contentHtml }}
          sx={{
            px: 2, py: 1.5,
            bgcolor: mine ? 'var(--color-primary-soft)' : 'var(--bg-page)',
            border: '1px solid',
            borderColor: mine ? 'var(--color-primary-soft)' : 'var(--divider)',
            borderRadius: 'var(--radius-card)',
            // canto "falado" do lado do autor
            borderTopRightRadius: mine ? 'var(--radius-sm)' : 'var(--radius-card)',
            borderTopLeftRadius: mine ? 'var(--radius-card)' : 'var(--radius-sm)',
            color: 'var(--text-primary)',
            fontSize: 'var(--fs-body2, .875rem)',
            lineHeight: 1.55,
            overflowWrap: 'anywhere',
            '& p': { m: 0 },
            '& p + p': { mt: 1 },
            '& img': { maxWidth: '100%', height: 'auto', borderRadius: 'var(--radius-sm)', display: 'block', mt: 1 },
            '& a': { color: 'var(--color-primary-dark)' },
            '& b, & strong': { fontWeight: 'var(--fw-bold)' },
          }}
        />
      </Box>
    </Box>
  );
}

/* ── Composer de resposta ─────────────────────────────────────────────── */
function ReplyComposer({ editor, a }) {
  const submitting = !!editor.submitting;
  const empty = !editor.content?.trim();
  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        borderTop: '1px solid', borderColor: 'var(--divider)',
        bgcolor: 'var(--surface)',
      }}
    >
      {editor.error && (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1.25,
            bgcolor: 'var(--tint-error-bg)', border: '1px solid', borderColor: 'var(--tint-error-border)',
            borderRadius: 'var(--radius-sm)', px: 1.5, py: 1,
          }}
        >
          <ErrorRounded sx={{ fontSize: 18, color: 'var(--tint-error-fg)' }} />
          <Typography variant="body2" sx={{ color: 'var(--tint-error-fg)' }}>{editor.error}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          value={editor.content || ''}
          onChange={(e) => a.onChangeReply?.(e.target.value)}
          disabled={submitting}
          placeholder="Escreva sua resposta ao suporte…"
          multiline
          minRows={1}
          maxRows={6}
          inputProps={{ 'aria-label': 'Sua resposta' }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 'var(--radius-card)', bgcolor: 'var(--bg-page)',
              '& fieldset': { borderColor: 'var(--divider)' },
            },
          }}
        />
        <Tooltip title="Enviar resposta">
          <span>
            <IconButton
              onClick={() => a.onSubmitReply?.()}
              disabled={submitting || empty}
              aria-label="Enviar resposta"
              sx={{
                width: 44, height: 44, flexShrink: 0,
                borderRadius: 'var(--radius-sm)',
                bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
                '&:hover': { bgcolor: 'var(--color-primary-dark)' },
                '&.Mui-disabled': { bgcolor: 'var(--bg-page)', color: 'var(--text-secondary)' },
              }}
            >
              {submitting
                ? <CircularProgress size={20} sx={{ color: 'var(--text-secondary)' }} />
                : <SendRounded sx={{ fontSize: 20 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {submitting && (
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mt: 0.75 }}>
          Enviando sua resposta…
        </Typography>
      )}
    </Box>
  );
}

/* ── Painel da conversa ───────────────────────────────────────────────── */
function ThreadPanel({ ticket, editor, a, onBack }) {
  const detail = ticket.detail;
  const scrollRef = useRef(null);
  // rola o fio para a mensagem mais recente ao trocar de ticket (UI-only)
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ticket.id, detail?.replies?.length]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      {/* cabeçalho da conversa */}
      <Box
        sx={{
          px: { xs: 2, md: 3 }, py: 2,
          borderBottom: '1px solid', borderColor: 'var(--divider)',
          display: 'flex', alignItems: 'flex-start', gap: 1.5,
        }}
      >
        <IconButton
          onClick={onBack}
          aria-label="Voltar para a lista de tickets"
          sx={{
            display: { xs: 'inline-flex', lg: 'none' }, width: 40, height: 40, flexShrink: 0,
            borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)',
            '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
          }}
        >
          <ArrowBackRounded sx={{ fontSize: 20 }} />
        </IconButton>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
            <StatusBadge label={ticket.statusLabel} statusTone={ticket.statusTone} size="lg" />
            <PriorityBadge priority={ticket.priority} />
            <TypeTag label={ticket.typeLabel} typeTone={ticket.typeTone} />
          </Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', lineHeight: 1.3, overflowWrap: 'anywhere' }}>
            {ticket.subject}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
              ID: {ticket.idShort}…
            </Typography>
            {detail?.createdAtLabel && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}>
                <ScheduleRounded sx={{ fontSize: 14, color: 'var(--text-secondary)' }} />
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                  Aberto em {detail.createdAtLabel}
                </Typography>
              </Box>
            )}
            {detail?.contactInfo && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375, minWidth: 0 }}>
                <AlternateEmailRounded sx={{ fontSize: 14, color: 'var(--text-secondary)', flexShrink: 0 }} />
                <Typography variant="caption" noWrap sx={{ color: 'var(--text-secondary)' }}>
                  {detail.contactInfo}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <IconButton
          onClick={() => a.onToggleTicket?.(null)}
          aria-label="Fechar conversa"
          sx={{
            display: { xs: 'none', lg: 'inline-flex' }, width: 40, height: 40, flexShrink: 0,
            borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)',
            '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
          }}
        >
          <ChevronRightRounded sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      {/* fio da conversa */}
      {ticket.detailsLoading || !detail ? (
        <ThreadSkeleton />
      ) : (
        <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2.5 }}>
          {/* mensagem original — âncora do fio */}
          <Box sx={{ position: 'relative', pl: { xs: 0, sm: 2.5 } }}>
            <Box
              aria-hidden
              sx={{
                display: { xs: 'none', sm: 'block' },
                position: 'absolute', left: 5, top: 10, bottom: -20, width: 2,
                bgcolor: 'var(--divider)',
              }}
            />
            <Box
              aria-hidden
              sx={{
                display: { xs: 'none', sm: 'block' },
                position: 'absolute', left: 0, top: 6, width: 12, height: 12, borderRadius: '50%',
                bgcolor: 'var(--color-primary)', boxShadow: '0 0 0 3px var(--color-primary-soft)',
              }}
            />
            <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.04em', textTransform: 'uppercase', mb: 0.75 }}>
              Mensagem original
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: 1.6,
                bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)',
                borderRadius: 'var(--radius-card)', p: 2, overflowWrap: 'anywhere',
              }}
            >
              {detail.message}
            </Typography>
          </Box>

          {detail.repliesEmpty ? (
            <Box
              sx={{
                mt: 3, py: 4, px: 2, textAlign: 'center',
                border: '1px dashed', borderColor: 'var(--divider)', borderRadius: 'var(--radius-card)',
              }}
            >
              <HourglassTopRounded sx={{ fontSize: 26, color: 'var(--text-secondary)' }} />
              <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)', mt: 0.5 }}>
                Ainda sem respostas
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mt: 0.25 }}>
                Nossa equipe já recebeu sua mensagem. Você pode complementar abaixo.
              </Typography>
            </Box>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 3, p: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {(detail.replies || []).map((r) => <ReplyBubble key={r.id} reply={r} />)}
            </Box>
          )}
        </Box>
      )}

      {editor?.visible && <ReplyComposer editor={editor} a={a} />}
    </Box>
  );
}

/* ── Painel vazio do master–detail (lg+) ──────────────────────────────── */
function NoSelectionPanel() {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 4, py: 8, textAlign: 'center' }}>
      <Box
        sx={{
          width: 72, height: 72, borderRadius: '50%', mb: 2,
          bgcolor: 'var(--color-primary-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <TouchAppRounded sx={{ fontSize: 32, color: 'var(--color-primary-dark)' }} />
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)' }}>
        Escolha uma conversa
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 320, mt: 0.5 }}>
        Selecione um ticket à esquerda para ler o histórico e responder ao suporte.
      </Typography>
    </Box>
  );
}

/* ── Blocos de estado de página inteira ───────────────────────────────── */
function StateBlock({ icon, title, description, action }) {
  return (
    <Box sx={{ ...CARD_SX, px: 3, py: { xs: 5, sm: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Box
        sx={{
          width: 64, height: 64, borderRadius: '50%', mb: 2,
          bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', mb: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 460 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/* ── Paginação da fila ────────────────────────────────────────────────── */
function ListPagination({ pagination, a }) {
  const page = pagination?.page || 1;
  const pageCount = pagination?.pageCount || 1;
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
        px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--bg-page)',
      }}
    >
      <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
        {pagination?.total} no total
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          onClick={() => a.onChangePage?.(page - 1)}
          disabled={page <= 1}
          aria-label="Página anterior de tickets"
          sx={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' } }}
        >
          <ChevronLeftRounded sx={{ fontSize: 20 }} />
        </IconButton>
        <Typography variant="caption" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap' }}>
          {page} / {pageCount}
        </Typography>
        <IconButton
          onClick={() => a.onChangePage?.(page + 1)}
          disabled={page >= pageCount}
          aria-label="Próxima página de tickets"
          sx={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' } }}
        >
          <ChevronRightRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

/* ── View ─────────────────────────────────────────────────────────────── */
export default function SupportTicketsView({ vm, actions }) {
  const a = actions || {};
  const tickets = vm.tickets || [];
  const open = tickets.find((t) => t.expanded) || null;

  const newButton = (
    <Button
      onClick={() => a.onNewTicket?.()}
      startIcon={<AddCommentRounded />}
      disableElevation
      sx={{
        bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
        borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 42,
        fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
        '&:hover': { bgcolor: 'var(--color-primary-dark)' },
      }}
    >
      {vm.newLabel}
    </Button>
  );

  const refreshButton = (
    <Button
      onClick={() => a.onRefresh?.()}
      disabled={!!vm.refreshing}
      startIcon={vm.refreshing
        ? <CircularProgress size={16} sx={{ color: 'var(--text-secondary)' }} />
        : <RefreshRounded sx={{ fontSize: 18 }} />}
      sx={{
        minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)',
        border: '1px solid', borderColor: 'var(--divider)', color: 'var(--text-secondary)',
        fontWeight: 'var(--fw-medium)', textTransform: 'none', whiteSpace: 'nowrap',
        '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
      }}
    >
      {vm.refreshLabel}
    </Button>
  );

  /* fila de conversas (master) */
  const queue = (
    <Box sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'var(--divider)',
          bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.06em', textTransform: 'uppercase' }}>
          Suas conversas
        </Typography>
        {vm.refreshing && <CircularProgress size={14} aria-label="Atualizando" sx={{ color: 'var(--text-secondary)' }} />}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box component="ul" sx={{ m: 0, p: 0 }}>
          {tickets.map((t) => (
            <TicketListItem
              key={t.id}
              ticket={t}
              selected={!!t.expanded}
              onSelect={() => a.onToggleTicket?.(t.expanded ? null : t.id)}
            />
          ))}
        </Box>
      </Box>

      {(vm.pagination?.pageCount || 1) > 1 && <ListPagination pagination={vm.pagination} a={a} />}
    </Box>
  );

  /* ── estados de página inteira ── */
  if (vm.viewState === 'loading') {
    return (
      <PageShell vm={vm} refreshButton={refreshButton} newButton={newButton}>
        <Box sx={{ ...CARD_SX, overflow: 'hidden', maxWidth: 520 }}>
          <ListSkeleton />
        </Box>
      </PageShell>
    );
  }

  if (vm.viewState === 'error') {
    return (
      <PageShell vm={vm} refreshButton={refreshButton} newButton={newButton}>
        <StateBlock
          icon={<ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} />}
          title="Não foi possível carregar seus tickets"
          description={vm.errorMessage}
          action={(
            <Button
              onClick={() => a.onRetry?.()}
              startIcon={<ReplayRounded />}
              sx={{
                minHeight: 42, px: 2.25, borderRadius: 'var(--radius-sm)',
                border: '1px solid', borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)',
                fontWeight: 'var(--fw-bold)', textTransform: 'none',
                '&:hover': { bgcolor: 'var(--color-primary-soft)' },
              }}
            >
              Tentar novamente
            </Button>
          )}
        />
      </PageShell>
    );
  }

  if (vm.viewState === 'empty') {
    return (
      <PageShell vm={vm} refreshButton={refreshButton} newButton={newButton}>
        <StateBlock
          icon={<SupportAgentRounded sx={{ fontSize: 32, color: 'var(--color-primary-dark)' }} />}
          title="Tudo tranquilo por aqui"
          description={vm.emptyMessage}
          action={newButton}
        />
      </PageShell>
    );
  }

  /* ── ready ── */
  return (
    <PageShell vm={vm} refreshButton={refreshButton} newButton={newButton}>
      {/* lg+ : master–detail com duas colunas de altura fixa e scroll independente */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'grid' },
          gridTemplateColumns: 'minmax(320px, 380px) minmax(0, 1fr)',
          gap: 'var(--space-2)',
          height: 'clamp(520px, calc(100vh - 260px), 820px)',
        }}
      >
        {queue}
        <Box sx={{ ...CARD_SX, minWidth: 0, overflow: 'hidden' }}>
          {open
            ? <ThreadPanel ticket={open} editor={vm.replyEditor} a={a} onBack={() => a.onToggleTicket?.(null)} />
            : <NoSelectionPanel />}
        </Box>
      </Box>

      {/* xs → md : uma coisa por vez — a conversa substitui a fila */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        {open ? (
          <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
            <ThreadPanel ticket={open} editor={vm.replyEditor} a={a} onBack={() => a.onToggleTicket?.(null)} />
          </Box>
        ) : queue}
      </Box>
    </PageShell>
  );
}

/* ── Moldura da página (header comum a todos os estados) ──────────────── */
function PageShell({ vm, refreshButton, newButton, children }) {
  return (
    <Box sx={{ color: 'var(--text-primary)' }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 2, flexWrap: 'wrap', mb: 'var(--space-3)',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupportAgentRounded sx={{ fontSize: 30, color: 'var(--color-primary-dark)' }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)' }}>
              {vm.title}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5 }}>
            {vm.subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {refreshButton}
          {newButton}
        </Box>
      </Box>
      {children}
    </Box>
  );
}
